/**
 * API client for the LessonFresh backend.
 *
 * Auth model: a short-lived session JWT (minted on LTI launch or Canvas
 * sign-in) held in sessionStorage — never cookies, because the app runs in a
 * Canvas iframe where third-party cookies are blocked. On 401 we exchange the
 * refresh token once and retry, so a 15-minute access token never surfaces
 * as a fake "not found" error mid-session.
 */

// API base: explicit env wins; on lessonfresh.com hosts the API lives at the
// matching api.* subdomain over https (dev tunnel / production layout);
// otherwise follow the host the page was loaded from (LAN IPs, localhost).
// Pure host→URL mapping shared by client code and SSR (server components pass
// the request's Host header) so server-rendered hrefs match hydration.
export function apiUrlForHost(host: string | null | undefined): string {
  if (process.env.NEXT_PUBLIC_API_URL) return process.env.NEXT_PUBLIC_API_URL;
  const hostname = (host ?? "").split(":")[0];
  if (!hostname) return "http://localhost:8001";
  // www is a redirect alias of the apex — never look for api.www.*
  if (hostname.endsWith("lessonfresh.com")) {
    return `https://api.${hostname.replace(/^www\./, "")}`;
  }
  return `http://${hostname}:8001`;
}

function resolveApiUrl(): string {
  if (typeof window === "undefined") return apiUrlForHost(null);
  return apiUrlForHost(window.location.hostname);
}
export const API_URL = resolveApiUrl();

const SESSION_KEY = "cf_session_token";
const REFRESH_KEY = "cf_refresh_token";

export function getSessionToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.sessionStorage.getItem(SESSION_KEY);
}

export function setSessionToken(token: string) {
  window.sessionStorage.setItem(SESSION_KEY, token);
}

export function clearSessionToken() {
  window.sessionStorage.removeItem(SESSION_KEY);
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

// Single-flight refresh: concurrent 401s share one exchange
let refreshing: Promise<boolean> | null = null;

async function tryRefreshSession(): Promise<boolean> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    const refreshToken = window.sessionStorage.getItem(REFRESH_KEY);
    if (!refreshToken) return false;
    try {
      const res = await fetch(`${API_URL}/api/v1/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) return false;
      const tokens = (await res.json()) as { access_token: string; refresh_token: string };
      setSessionToken(tokens.access_token);
      window.sessionStorage.setItem(REFRESH_KEY, tokens.refresh_token);
      return true;
    } catch {
      return false;
    } finally {
      // Allow the next expiry (in 15 min) to refresh again
      setTimeout(() => {
        refreshing = null;
      }, 0);
    }
  })();
  return refreshing;
}

/** Authenticated fetch with one automatic refresh-and-retry on 401. */
export async function authFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const send = () => {
    const headers = new Headers(init.headers);
    const token = getSessionToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
    return fetch(`${API_URL}${path}`, { ...init, headers });
  };
  let res = await send();
  if (res.status === 401 && (await tryRefreshSession())) {
    res = await send();
  }
  return res;
}

/** Multipart upload — no explicit Content-Type so the browser sets the boundary. */
export async function apiUpload<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append("file", file);
  const res = await authFetch(path, { method: "POST", body: form });
  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(res.status, body || res.statusText);
  }
  return res.json() as Promise<T>;
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  const res = await authFetch(path, { ...init, headers });
  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(res.status, body || res.statusText);
  }
  return res.json() as Promise<T>;
}
